import type { ProvisioningAmpSetting, ProvisioningPlan, ProvisioningScheduleEntry, ProvisioningSettingValue } from '../types/modpackProvisioning';
import { migrationBytes } from './modpackMigration';

const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const number = (value: unknown): number | null => (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') && Number.isFinite(Number(value)) ? Number(value) : null;
const selections = (value: unknown, max: number, min = 0): number[] | null => Array.isArray(value) && value.length > 0
  && value.every(item => Number.isInteger(item) && item >= min && item <= max) ? [...new Set(value as number[])].sort((a, b) => a - b) : null;
const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (value: number) => String(value).padStart(2, '0');
const amount = (value: unknown, suffix = '') => {
  const parsed = number(value);
  return parsed === null ? 'Not reported' : `${parsed.toLocaleString('en-US')}${suffix}`;
};

export function provisioningBackupSummary(schedule: Record<string, unknown>, entries?: ProvisioningScheduleEntry[]): Record<string, unknown> {
  const hours = selections(schedule.hours, 23), minutes = selections(schedule.minutes, 59);
  const weekdays = selections(schedule.days, 6), selectedMonths = selections(schedule.months, 11), monthDays = selections(schedule.days_of_month, 31, 1);
  let timing = 'See schedule details';
  if (hours && minutes) {
    timing = hours.length * minutes.length <= 16
      ? hours.flatMap(hour => minutes.map(minute => `${pad(hour)}:${pad(minute)}`)).join(', ')
      : hours.length === 24 ? `Every hour at minute ${minutes.map(pad).join(', ')}`
        : `Hours ${hours.map(pad).join(', ')}; minutes ${minutes.map(pad).join(', ')}`;
  }
  const exactTimes = selections(schedule.times, 1439);
  if (exactTimes) timing = provisioningTimes(exactTimes);
  const actionTimes = entries?.filter(entry => entry.kind === 'time').flatMap(entry => entry.actions
    .filter(action => action.category === 'backup').map(action => provisioningTimes(entry.times ?? [], action.offset_seconds)));
  if (actionTimes?.length) timing = [...new Set(actionTimes)].join('; ');
  const retention = record(schedule.retention), stagger = record(schedule.stagger), state = number(schedule.enabled_state);
  const compression = retention['Limits.CompressionLevel'];
  return {
    'Local times': timing,
    Days: weekdays?.length === 7 ? 'Every day' : weekdays?.map(day => days[day]).join(', ') ?? 'See schedule details',
    ...(monthDays ? { 'Days of month': monthDays.join(', ') } : {}),
    ...(selectedMonths?.length !== 12 ? { Months: selectedMonths?.map(month => months[month]).join(', ') ?? 'See schedule details' } : {}),
    'Time zone': schedule.timezone,
    ...(state !== null ? { Schedule: (state & 1) === 1 ? 'Enabled' : 'Disabled' } : {}),
    'Backup count limit': amount(retention['Limits.MaxBackupCount'] ?? schedule.retention_count),
    'Total backup space limit': amount(retention['Limits.MaxTotalSizeMB'], ' MB'),
    'Single backup size limit': amount(retention['Limits.MaxIndividualSizeMB'], ' MB'),
    Compression: number(compression) !== null ? `Level ${compression}` : typeof compression === 'string'
      ? ({ SmallestSize: 'Smallest size', NoCompression: 'No compression' } as Record<string, string>)[compression] ?? compression : 'Not reported',
    ...(number(schedule.estimated_duration_minutes) !== null ? { 'Estimated backup time': amount(schedule.estimated_duration_minutes, ' minutes') } : {}),
    ...(number(stagger.backup_shift_minutes) ? { 'Schedule adjustment': `Shifted ${amount(stagger.backup_shift_minutes, ' minutes')}` } : {}),
    ...(number(stagger.predicted_overlap_minutes) !== null && Number(stagger.predicted_overlap_minutes) > 0
      ? { 'Overlap with other backups': amount(stagger.predicted_overlap_minutes, ' minutes') } : {}),
    ...(Array.isArray(stagger.review_irregular) && stagger.review_irregular.length ? { 'Schedule review': 'Irregular timing was kept. Check for overlap with other backups.' } : {}),
  };
}

const clockTime = (seconds: number) => {
  const rounded = Math.round(seconds), time = ((rounded % 86400) + 86400) % 86400;
  return `${pad(Math.floor(time / 3600))}:${pad(Math.floor(time % 3600 / 60))}${time % 60 ? `:${pad(time % 60)}` : ''}`;
};

/** Host times are local minute-of-day values. Positive or negative action offsets may cross midnight. */
export function provisioningTimes(times: number[], offsetSeconds = 0): string {
  const valid = selections(times, 1439);
  if (!valid || !Number.isFinite(offsetSeconds)) return 'Timing not reported';
  const seconds = [...new Set(valid.map(minute => ((minute * 60 + offsetSeconds) % 86400 + 86400) % 86400))].sort((a, b) => a - b);
  if (seconds.length > 12) {
    const intervals = seconds.map((time, index) => (seconds[(index + 1) % seconds.length] - time + 86400) % 86400);
    if (intervals.every(interval => interval === intervals[0]) && intervals[0] % 60 === 0) {
      return `Every ${intervals[0] / 60} minutes, starting ${clockTime(seconds[0])}`;
    }
  }
  return seconds.map(clockTime).join(', ');
}

const settingLabels: Record<string, string> = {
  ServerMOTD: 'MOTD',
  AllowCommandBlocks: 'Command blocks', AllowFlight: 'Flight', BroadcastConsoleToOps: 'Console messages to operators', BroadcastRconToOps: 'RCON messages to operators',
  ChatSpamThresholdSeconds: 'Chat cooldown (seconds)', CommandSpamThresholdSeconds: 'Command cooldown (seconds)', Difficulty: 'Difficulty', EnableAnimals: 'Animals',
  EnableMonsters: 'Monsters', EnableNPCs: 'NPCs', EnablePVPCombat: 'PvP', ForceGameMode: 'Enforce game mode', FuncPermissions: 'Function permission level', GameMode: 'Game mode',
  HardcoreMode: 'Hardcore', MaxTickTime: 'Watchdog timeout', OpPermssions: 'Operator permission level', SpawnProtectionRadius: 'Spawn protection', TimeoutMins: 'Idle timeout (minutes)',
  EnableStatus: 'Status query', EnforceSecureProfile: 'Secure profiles', EntityBroadcastRange: 'Entity broadcast range (%)', HideOnlinePlayers: 'Hide online players',
  NetworkCompressionThreshold: 'Network compression threshold', Compression: 'Region file compression', RestartOnSevere: 'Restart on severe errors', SimulationDistance: 'Simulation distance',
  SyncChunkWrites: 'Synchronous chunk writes', UseNativeTransport: 'Native network transport', ViewDistance: 'View distance',
  MaxChainedNeigbourUpdates: 'Chained neighbour update limit', StopTimeout: 'Stop timeout', MaxPlayers: 'Player limit', EnableAnalytics: 'AMP server analytics',
};
const primarySettings = new Set(['ServerMOTD', 'AllowCommandBlocks', 'AllowFlight', 'ForceGameMode', 'GameMode', 'Difficulty', 'SimulationDistance', 'ViewDistance', 'Compression', 'SyncChunkWrites', 'SpawnProtectionRadius', 'MaxTickTime', 'EnableAnalytics']);
const readableName = (value: string) => value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2').replace(/_/g, ' ');
const settingValue = (name: string, value: ProvisioningSettingValue): unknown => {
  if (name === 'ServerMOTD') return value;
  if (value === null) return 'Unset';
  const numeric = number(value);
  return /^true$/i.test(String(value)) ? 'Enabled' : /^false$/i.test(String(value)) ? 'Disabled'
    : name === 'Compression' && numeric !== null ? ['Deflate', 'LZ4', 'None'][numeric] ?? value
    : name === 'Difficulty' && numeric !== null ? ['Peaceful', 'Easy', 'Normal', 'Hard'][numeric] ?? value
      : name === 'MaxTickTime' && numeric !== null ? numeric < 0 ? 'Disabled' : `${numeric / 1000} seconds`
        : ['SimulationDistance', 'ViewDistance'].includes(name) ? `${value} chunks`
          : name === 'SpawnProtectionRadius' ? `${value} blocks` : value === '' ? '(empty)' : value;
};
export function provisioningAmpSettings(plan: ProvisioningPlan, all = false): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const [node, value] of Object.entries(plan.amp_policy?.settings ?? {})) {
    if (plan.amp_policy?.entries?.some(entry => entry.node === node && ['private', 'excluded'].includes(entry.treatment))) continue;
    const name = node.split('.').pop()!;
    if (name === 'ServerMOTD' && plan.amp_policy?.motd && !all) continue;
    if (!all && !primarySettings.has(name)) continue;
    values[settingLabels[name] ?? readableName(name)] = settingValue(name, value);
  }
  return values;
}

const sectionLabels: Record<string, string> = { 'Core.AMP': 'AMP', 'Core.Webserver': 'Web server', 'Core.Login': 'Sign-in',
  'MinecraftModule.Minecraft': 'Minecraft', 'MinecraftModule.Game': 'Gameplay', 'MinecraftModule.Java': 'Java', 'MinecraftModule.Limits': 'Server limits',
  'LocalFileBackupPlugin.Storage': 'Backup storage', 'LocalFileBackupPlugin.Limits': 'Backup limits', 'FileManagerPlugin.SFTP': 'SFTP' };
export function provisioningAmpGroups(plan: ProvisioningPlan) {
  const groups = new Map<string, { title: string; settings: { node: string; label: string; treatment: ProvisioningAmpSetting['treatment']; value: unknown; reason: string }[] }>();
  const entries: ProvisioningAmpSetting[] = plan.amp_policy?.entries ?? Object.entries(plan.amp_policy?.settings ?? {}).map(([node, value]) => ({ node, value, treatment: 'inherited', source_value_digest: '', reason: '' }));
  for (const entry of entries) {
    const parts = entry.node.split('.'), name = parts.pop()!, section = parts.join('.');
    const title = entry.section ? readableName(entry.section.replace(/(?:Module|Plugin)$/, '')) : sectionLabels[section] || parts.map(part => readableName(part.replace(/(?:Module|Plugin)$/, ''))).join(' · ');
    const group = groups.get(title) ?? { title, settings: [] };
    const value = entry.value !== undefined ? entry.value : plan.amp_policy?.settings[entry.node];
    let structured: unknown;
    if (entry.value_format === 'structured' && typeof value === 'string' && !['private', 'excluded'].includes(entry.treatment)) {
      try { const parsed: unknown = JSON.parse(value); if (parsed !== null && typeof parsed === 'object') structured = parsed; } catch { /* Preserve the supplied value if it is not valid JSON. */ }
    }
    group.settings.push({ node: entry.node, label: entry.label && entry.label !== name ? readableName(entry.label) : settingLabels[name] || readableName(name), treatment: entry.treatment,
      value: entry.treatment === 'private' ? 'Private value retained on the host' : entry.treatment === 'excluded' ? 'Not copied'
        : value === undefined ? 'Not reported' : structured ?? settingValue(name, value), reason: entry.reason });
    groups.set(title, group);
  }
  return [...groups.values()];
}

export function provisioningAmpCounts(plan: ProvisioningPlan) {
  const entries = plan.amp_policy?.entries;
  if (!entries) return null;
  return { total: entries.length, inherited: entries.filter(entry => entry.treatment === 'inherited').length,
    derived: entries.filter(entry => entry.treatment === 'derived').length, private: entries.filter(entry => entry.treatment === 'private').length,
    excluded: entries.filter(entry => entry.treatment === 'excluded').length };
}

export function provisioningJavaSummary(plan: ProvisioningPlan): Record<string, unknown> {
  return { Java: plan.java.major, ...(typeof plan.java.minimum_heap_mb === 'number' ? { 'Minimum heap': migrationBytes(plan.java.minimum_heap_mb * 1024 ** 2) } : {}),
    'Maximum heap': migrationBytes(plan.memory_bytes), 'JVM arguments': plan.java.jvm_args || 'Java defaults' };
}

export function provisioningSftpSummary(plan: ProvisioningPlan): string | null {
  if (typeof plan.network.sftp_enabled !== 'boolean') return null;
  if (!plan.network.sftp_enabled) return 'Disabled';
  return plan.network.sftp_binding ? `Tailscale only · ${plan.network.sftp_binding}:${plan.ports.sftp}` : `Enabled (port ${plan.ports.sftp})`;
}

const eventLabels: Record<string, string> = {
  'Event.LocalFileBackupPlugin.BackupStarting': 'When a backup starts', 'Event.LocalFileBackupPlugin.BackupFailed': 'If a backup fails',
  'Event.LocalFileBackupPlugin.TakeBackupComplete': 'When a backup finishes', 'Event.MinecraftModule.UserRejoins': 'When a player returns',
  'Event.MinecraftModule.UserFirstJoins': 'When a player first joins', 'Event.MinecraftModule.UnexpectedStop': 'If Minecraft stops unexpectedly',
  'Event.MinecraftModule.WatchdogShutdown': 'After a watchdog shutdown',
};
export const provisioningScheduleEvent = (event: string | null | undefined) => event ? eventLabels[event] ?? 'Template event' : 'Timed schedule';
const actionLabels: Record<string, string> = { backup: 'Backup', restart: 'Restart', start: 'Start Minecraft', stop: 'Stop Minecraft', save: 'Save world', cleanup: 'Remove dropped items',
  notification: 'Player message', kick: 'Disconnect players', maintenance: 'Ticking entity recovery', custom: 'Custom action', update: 'Update' };
export function provisioningScheduleSummary(entry: ProvisioningScheduleEntry) {
  const actions = [...entry.actions].sort((a, b) => a.order - b.order).filter(action => action.category !== 'wait');
  const work = actions.filter(action => action.category !== 'notification');
  const title = [...new Set((work.length ? work : actions).map(action => actionLabels[action.category] ?? 'Template action'))].join(' and ') || 'Template schedule';
  return { title, trigger: entry.kind === 'event' ? provisioningScheduleEvent(entry.event) : 'Every day',
    actions: actions.map(action => ({ category: action.category, label: actionLabels[action.category] ?? 'Template action',
      timing: entry.kind === 'time' ? provisioningTimes(entry.times ?? [], action.offset_seconds) : action.offset_seconds ? `After ${action.offset_seconds} seconds` : 'Immediately',
      ...(action.category === 'cleanup' ? { scope: 'Dropped items only' } : {}),
      ...(action.duration_minutes > 0 ? { duration: `${action.duration_minutes} minute${action.duration_minutes === 1 ? '' : 's'} estimated` } : {}),
    })),
    intervals: entry.intervals_minutes?.length && new Set(entry.intervals_minutes).size > 1
      ? `Intervals: ${entry.intervals_minutes.map(value => `${Math.floor(value / 60)}h${value % 60 ? ` ${value % 60}m` : ''}`).join(', ')}` : null };
}

export function provisioningQuasarSummary(plan: ProvisioningPlan): Record<string, unknown> {
  const quasar = plan.quasar;
  const config = typeof quasar.config_path === 'string' ? quasar.config_path : null;
  const game = plan.game_root.replace(/\/$/, '');
  return {
    Version: quasar.version,
    Selection: quasar.latest_compatible === true ? 'Latest compatible release' : 'Compatibility not reported',
    Minecraft: quasar.minecraft,
    Modloader: quasar.loader,
    'Server address': quasar.fqdn,
    'Live config': config ? `${game}/${config}` : undefined,
    'Template config': config ? `${game}/templates/${config}` : undefined,
    ...(typeof quasar.filename === 'string' ? { 'Mod file': `${game}/mods/${quasar.filename}` } : {}),
  };
}

export function provisioningRoutingSummary(value: unknown): Record<string, unknown>[] {
  const plan = record(value);
  return (Array.isArray(plan.servers) ? plan.servers : []).map(record).filter(server => typeof server.server_address === 'string')
    .map(server => ({
      'Server address': server.server_address,
      Backend: typeof server.public_ip === 'string' && typeof server.public_port === 'number' ? `${server.public_ip}:${server.public_port}` : undefined,
      'TCPShield CNAME': plan.protected_cname,
      DNS: server.dns === 'existing_cname' ? 'Existing CNAME' : 'Not reported',
    }));
}
